// Product Tracking: daily PDP scrape of the client's own and competitor products on Amazon, Flipkart
// and Myntra, served by reportsapi POST /client/{clientId}/{marketplace}:scrape-insights (one cube per
// marketplace); comparison view uses {marketplace}:scrape-insights-comparison (<measure>,
// <measure>_comparison, <measure>_change_percentage). The lists below are Amazon's; productTrackingConfigFor
// drops the Amazon-only fields and names the marketplace for the others.

export const PRODUCT_TRACKING_PAGE_SIZE = 50;

// Clients subscribed to Product Tracking (Parse client ids). Everyone else sees the sidebar item locked,
// with PRODUCT_TRACKING_LOCKED_MESSAGE on hover, and the same message on the page. Add a client here to
// unlock it.
export const PRODUCT_TRACKING_ENABLED_CLIENT_IDS = ["Hszti7RKkJ", "evWhrYZMdP", "IFdQRGGmRJ", "jL8EXMlw3C"];
export const PRODUCT_TRACKING_LOCKED_MESSAGE =
  "This platform’s product tracking requires a paid subscription. Reach out to our team for details.";
export const isProductTrackingEnabled = (clientId) => PRODUCT_TRACKING_ENABLED_CLIENT_IDS.includes(clientId);

// "Analysis by": every dimension of the cube. `searchable` = free-text search on that dimension.
export const PRODUCT_TRACKING_ANALYSIS_OPTIONS = [
  { value: "productId", label: "Article", searchable: true },
  { value: "date", label: "Date", searchable: false },
  { value: "brand", label: "Brand", searchable: true },
  { value: "sellerName", label: "Buy Box Seller", searchable: true },
  { value: "productType", label: "Product Type", searchable: true },
  { value: "leafCategory", label: "Category", searchable: true },
  { value: "listingStatus", label: "Listing Status", searchable: true },
  { value: "availability", label: "Stock Status", searchable: true },
  { value: "dealBadge", label: "Deal Tag", searchable: true },
  { value: "city", label: "City", searchable: true },
  { value: "buyboxFulfilledBy", label: "Fulfilled By", searchable: true },
  { value: "pincodeStatus", label: "Pincode Status", searchable: true },
  { value: "status", label: "Scrape Status", searchable: true },
  { value: "runId", label: "Scrape Run", searchable: true },
];

// Table columns: measures that work for any "Analysis by" and for the summary row.
// format: currency | percent | decimal | integer
export const PRODUCT_TRACKING_METRIC_COLUMNS = [
  { key: "productCount", label: "Products", format: "integer" },
  { key: "avgDiscountPct", label: "Avg Discount", format: "percent" },
  { key: "inStockRate", label: "In Stock", format: "percent" },
  { key: "avgRating", label: "Avg Rating", format: "decimal" },
  { key: "avgReviewCount", label: "Avg Review Count", format: "integer" },
  { key: "avgLqs", label: "Avg LQS", format: "percent", decimals: 0 },
  { key: "dealCount", label: "Deals", format: "integer" },
  { key: "avgSellingPrice", label: "Avg Selling Price", format: "currency" },
  { key: "avgMrp", label: "Avg MRP", format: "currency" },
  { key: "avgBsr", label: "Avg BSR", format: "integer", lowerIsBetter: true },
  { key: "avgDeliveryDays", label: "Fulfilment Days", format: "decimal", lowerIsBetter: true },
];

// "Analysis by" ASIN: the product's own values, in the PM's column order (Product ID and title are
// in the first column). `summaryKey` = what the summary row shows in that column; format "text" /
// "sizes" / "status" are rendered as-is / as size chips / as a status label. `decimals` caps the
// fraction digits; `tooltip` is the header's hover text.
export const PRODUCT_TRACKING_ASIN_COLUMNS = [
  { key: "brand", label: "Brand", format: "text", sortable: true, tooltip: "Brand name as shown on the Amazon listing." },
  { key: "listingStatus", label: "Listing Status", format: "status", sortable: true, tooltip: "Whether the listing is Live or Inactive (page loaded with no product)." },
  { key: "bsr", label: "BSR", format: "integer", sortable: true, lowerIsBetter: true, tooltip: "Rank within the product's lowest-level (child) category on Amazon." },
  { key: "sellerName", label: "Buy Box Seller", format: "text", sortable: true, tooltip: "Seller currently holding the Buy Box for this listing." },
  { key: "availability", label: "Stock Status", format: "status", sortable: true, summaryKey: "inStockRate", summaryFormat: "percent", tooltip: "Whether the product is In Stock or Out of Stock (OOS) at the selected pincode." },
  { key: "sizes", label: "Size", format: "sizes", sortable: false, tooltip: "Size variants of the listing, as shown on Amazon. Green = in stock, crossed out = out of stock." },
  { key: "discountPct", label: "Discount %", format: "percent", decimals: 1, sortable: true, summaryKey: "avgDiscountPct", tooltip: "Discount currently shown on Amazon, calculated against the listed MRP." },
  { key: "rating", label: "Ratings", format: "decimal", decimals: 1, sortable: true, summaryKey: "avgRating", tooltip: "Average star rating (out of 5) as of the last data refresh." },
  { key: "reviewCount", label: "Reviews count", format: "integer", sortable: true, summaryKey: "avgReviewCount", tooltip: "Total number of customer reviews on the product." },
  // Tooltip per marketplace (lqsTooltip): the checks differ.
  { key: "lqs", label: "LQS", format: "percent", decimals: 0, sortable: true, summaryKey: "avgLqs" },
  { key: "dealBadge", label: "Deal Tag", format: "text", sortable: true, summaryKey: "dealCount", summaryFormat: "deals", tooltip: "Deal badge on the listing." },
  { key: "city", label: "City", format: "text", sortable: true, tooltip: "City (pincode) the data was collected for." },
];

// Display labels for status-like values.
export const PRODUCT_TRACKING_STATUS_LABELS = {
  IN_STOCK: "In Stock",
  OOS: "OOS",
  UNKNOWN: "Unknown",
  Live: "Live",
  Inactive: "Inactive",
};

// Metric columns that would repeat an ASIN column (or are always 1 per ASIN) are hidden in the ASIN view.
export const PRODUCT_TRACKING_ASIN_HIDDEN_METRICS = [
  "productCount",
  "avgDiscountPct",
  "avgRating",
  "avgReviewCount",
  "avgLqs",
  "dealCount",
  "avgBsr",
  "inStockRate",
];

// Extra per-row fields shown inside the product cell.
// lqsFailedChecks: JSON list of the LQS checks the listing failed, shown on the LQS cell's info icon.
export const PRODUCT_TRACKING_ASIN_DETAIL_MEASURES = ["title", "productType", "imageUrl", "lqsFailedChecks"];

// Backend export (adyogiadsapi reports:export): its report type, and the product-cell fields that get
// their own columns in the file in the product view.
export const PRODUCT_TRACKING_EXPORT_REPORT_TYPE = "PRODUCT_TRACKING";
export const PRODUCT_TRACKING_EXPORT_DETAIL_FIELDS = ["title", "productType"];

// Sort used when an "Analysis by" is picked (others: productCount desc).
export const PRODUCT_TRACKING_DEFAULT_ORDER = { productId: ["reviewCount", "desc"], date: ["date", "asc"] };

// Quick filters (Filters panel): the 7 most used; everything else is in Advanced filters. chips: pick values; search: pick from the values in the date range;
// metric: >= / <= a number on each product-day (row-level, before aggregation). `key` is the API member.
export const PRODUCT_TRACKING_QUICK_FILTERS = [
  {
    key: "productType",
    label: "Product Type",
    kind: "chips",
    options: [
      { value: "self", label: "Self" },
      { value: "other", label: "Competitor" },
    ],
  },
  {
    key: "availability",
    label: "Stock Status",
    kind: "chips",
    options: [
      { value: "IN_STOCK", label: "In Stock" },
      { value: "OOS", label: "OOS" },
      { value: "UNKNOWN", label: "Unknown" },
    ],
  },
  {
    key: "listingStatus",
    label: "Listing Status",
    kind: "chips",
    options: [
      { value: "Live", label: "Live" },
      { value: "Inactive", label: "Inactive" },
    ],
  },
  { key: "discountPctValue", label: "Discount %", kind: "metric" },
  { key: "ratingValue", label: "Ratings", kind: "metric" },
  { key: "brand", label: "Brand", kind: "search" },
  { key: "leafCategory", label: "Category", kind: "search" },
  // sizeList is "|S|M|L|" per listing: a product matches when it has any selected size.
  { key: "sizeList", label: "Size", kind: "search", listDelimiter: "|" },
];

// Advanced filters (ComplexFilterWidget): Metric = aggregate measures (per row of the table),
// Attribute = dimensions. Field shape follows the widget's config (see PerformanceAnalysis).
const metricField = (id, name) => ({
  name,
  id,
  datatype: "int",
  type: "default",
  placeholder: "Enter value",
  dynamicOptions: false,
  autoComplete: true,
  source: "Adyogi",
  apiKey: `${id}_key`,
  grouping: name,
});
const textField = (id, name) => ({ ...metricField(id, name), datatype: "string" });
const optionField = (id, name, options) => ({ ...textField(id, name), optionsType: "Static", optionsKey: options });

export const PRODUCT_TRACKING_ADVANCED_FILTER_FIELDS = {
  measure: [
    metricField("productCount", "Products"),
    metricField("avgDiscountPct", "Avg Discount %"),
    metricField("inStockRate", "In Stock %"),
    metricField("avgRating", "Avg Ratings"),
    metricField("avgReviewCount", "Avg Reviews count"),
    metricField("dealCount", "Deals"),
    metricField("avgSellingPrice", "Avg Selling Price"),
    metricField("avgMrp", "Avg MRP"),
    metricField("avgBsr", "Avg BSR"),
    metricField("avgDeliveryDays", "Fulfilment Days"),
  ],
  dimension: [
    textField("brand", "Brand"),
    textField("productId", "Product ID"),
    textField("sellerName", "Buy Box Seller"),
    textField("leafCategory", "Category"),
    optionField("productType", "Product Type", "self,other"),
    optionField("availability", "Stock Status", "IN_STOCK,OOS,UNKNOWN"),
    optionField("listingStatus", "Listing Status", "Live,Inactive"),
    textField("dealBadge", "Deal Tag"),
    textField("buyboxFulfilledBy", "Fulfilled By"),
    textField("city", "City"),
    optionField("status", "Scrape Status", "ready,error"),
    metricField("discountPctValue", "Discount %"),
    metricField("ratingValue", "Ratings"),
    metricField("reviewCountValue", "Reviews count"),
    metricField("bsrValue", "BSR"),
    metricField("sellingPriceValue", "Selling Price"),
  ],
};

// "Analysis by" values whose rows get the trend button.
export const PRODUCT_TRACKING_TREND_DIMENSIONS = ["productId", "brand", "sellerName", "leafCategory"];

// Category trend (button in the first column's header): lines per category, top N by products.
export const PRODUCT_TRACKING_CATEGORY_TREND_LIMIT = 5;

// Trend dialog metrics (per day). For one ASIN an average is that day's value.
export const PRODUCT_TRACKING_TREND_METRICS = [
  { key: "avgSellingPrice", label: "Selling Price", format: "currency" },
  { key: "avgMrp", label: "MRP", format: "currency" },
  { key: "avgDiscountPct", label: "Discount %", format: "percent" },
  { key: "avgBsr", label: "BSR", format: "integer" },
  { key: "avgRating", label: "Rating", format: "decimal" },
  { key: "avgReviewCount", label: "Review Count", format: "integer" },
  { key: "inStockRate", label: "In Stock %", format: "percent" },
  { key: "avgDeliveryDays", label: "Fulfilment Days", format: "decimal" },
  { key: "dealCount", label: "Deals", format: "integer" },
  { key: "productCount", label: "Products", format: "integer", hideForAsin: true },
];

export const PRODUCT_TYPE_LABELS = { self: "Self", other: "Competitor" };

// Per marketplace: display name, what its product id is called, the listing's URL (the scraper's
// canonical one), and how Listing Status is decided (the PM's rule, applied in that marketplace's cube).
export const PRODUCT_TRACKING_MARKETPLACES = {
  amazon: {
    name: "Amazon",
    lqsTooltip: "Listing Quality Score: % of the listing team's audit checks the listing passes. Title 65-75 characters, 5+ bullets, description incl. A+ text 600+ characters, 5+ images, 100+ reviews, rating 4.0+, video, A+ content.",
    idLabel: "ASIN",
    productUrl: (id) => `https://www.amazon.in/dp/${encodeURIComponent(id)}`,
    listingStatusTooltip: "Whether the listing is Live or Inactive (page loaded with no product).",
  },
  flipkart: {
    name: "Flipkart",
    lqsTooltip: "Listing Quality Score: % of the listing team's audit checks the listing passes. Description 600+ characters, 5+ images, 100+ reviews, rating 4.0+. (Video not checked yet.)",
    idLabel: "FSN",
    productUrl: (id) => `https://www.flipkart.com/product/p/itme?pid=${encodeURIComponent(id)}`,
    listingStatusTooltip: "Whether the listing is Live or Inactive (no MRP and no size in stock).",
  },
  myntra: {
    name: "Myntra",
    lqsTooltip: "Listing Quality Score: % of the listing team's audit checks the listing passes. Product name 70+ characters, product details 700+ characters, 5+ images, 100+ reviews, rating 4.0+. (Video not checked yet.)",
    idLabel: "Style ID",
    productUrl: (id) => `https://www.myntra.com/${encodeURIComponent(id)}`,
    listingStatusTooltip: "Whether the listing is Live or Inactive (no size in stock and the page shows Out of Stock).",
  },
};

// Fields only Amazon has (BSR, deal badge, Buy Box fulfilment); the PM skips them for Flipkart and Myntra.
const AMAZON_ONLY_FIELDS = ["bsr", "avgBsr", "bsrValue", "dealBadge", "dealCount", "buyboxFulfilledBy"];

// Every list above, for one marketplace ("amazon" | "flipkart" | "myntra").
export const productTrackingConfigFor = (marketplace) => {
  const info = PRODUCT_TRACKING_MARKETPLACES[marketplace] ?? PRODUCT_TRACKING_MARKETPLACES.amazon;
  const keep = (field) => marketplace === "amazon" || !AMAZON_ONLY_FIELDS.includes(field);
  const named = (tooltip) => tooltip?.replace(/Amazon/g, info.name);
  const productIdName = `Product ID (${info.idLabel})`;
  return {
    ...info,
    marketplace,
    analysisOptions: PRODUCT_TRACKING_ANALYSIS_OPTIONS.filter((o) => keep(o.value)).map((o) =>
      o.value === "productId" ? { ...o, label: `Article (${info.idLabel})` } : o
    ),
    metricColumns: PRODUCT_TRACKING_METRIC_COLUMNS.filter((c) => keep(c.key)),
    asinColumns: PRODUCT_TRACKING_ASIN_COLUMNS.filter((c) => keep(c.key)).map((c) => ({
      ...c,
      tooltip: { listingStatus: info.listingStatusTooltip, lqs: info.lqsTooltip }[c.key] ?? named(c.tooltip),
    })),
    advancedFilterFields: {
      measure: PRODUCT_TRACKING_ADVANCED_FILTER_FIELDS.measure.filter((f) => keep(f.id)),
      dimension: PRODUCT_TRACKING_ADVANCED_FILTER_FIELDS.dimension
        .filter((f) => keep(f.id))
        .map((f) => (f.id === "productId" ? { ...f, name: productIdName, grouping: productIdName } : f)),
    },
    trendMetrics: PRODUCT_TRACKING_TREND_METRICS.filter((m) => keep(m.key)),
  };
};
